import React, { useEffect, useState } from "react";

import { LoadingScreen } from "@administrate/piston-ux";
import * as Sentry from "@sentry/browser";
import { dedupeIntegration } from "@sentry/integrations";
import { ApolloError } from "apollo-boost";
import { throwIfNotOK } from "../fetchHelpers";
import { asDiagnostic } from "../utils/errorHelpers";
import { GlobalStoreProvider } from "./GlobalStore";
import { Config } from "./config";

type Environment = "development" | "staging" | "production" | "unknown";

const getEnvironmentFromApiUrl = (apiUrl: string): Environment => {
  if (apiUrl.includes(".devadministratehq.com")) {
    return "development";
  }
  if (apiUrl.includes(".stagingadministratehq.com")) {
    return "staging";
  }
  if (apiUrl.includes(".administratehq.com")) {
    return "production";
  }
  return "unknown";
};

export const loadConfig = async (): Promise<Config> =>
  fetch("/config")
    .then(throwIfNotOK)
    .then(result => result.json());

export const ConfigLoader: React.FunctionComponent = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    loadConfig()
      .then(config => {
        if (config.ui_sentry_dsn) {
          Sentry.init({
            dsn: config.ui_sentry_dsn,
            denyUrls: [/\/files\/[a-f0-9]{128}/i],
            integrations: [dedupeIntegration()],
            environment: getEnvironmentFromApiUrl(config.graphql_uri),
            beforeSend: (event, hint) => {
              const exception = hint?.originalException;
              if (exception instanceof ApolloError) {
                event.fingerprint = [
                  exception.networkError ? "Network" : "GraphQL",
                  exception.message,
                ];
              }
              return event;
            },
          });
        }

        setConfig(config);
      })
      .catch(
        asDiagnostic(
          "Config",
          document.location.origin + "/config",
          "Failed to Fetch",
        ),
      );
  }, []);

  if (!config) {
    return <LoadingScreen />;
  }

  return <GlobalStoreProvider config={config}>{children}</GlobalStoreProvider>;
};
